/**
 * Cognito Authentication Service
 * Handles all Cognito API calls to backend
 * Includes Google OAuth integration
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com';

/**
 * Sign up a new user
 * @param {string} email
 * @param {string} password
 * @param {object|string} userAttributes - User attributes or display name
 * @param {object} options - Optional request options
 */
export const signup = async (email, password, userAttributes = {}, options = {}) => {
  const attributes = typeof userAttributes === 'string'
    ? { name: userAttributes }
    : (userAttributes || {});
  const headers = {};

  if (options.includeAuth) {
    const accessToken = localStorage.getItem('cognitoAccessToken');
    const idToken = localStorage.getItem('cognitoIdToken');

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    if (idToken) {
      headers['x-id-token'] = idToken;
    }
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/signup`, {
    email,
    password,
    ...attributes,
  }, {
    headers,
  });
  return response.data;
};

/**
 * Confirm signup with OTP code
 * @param {string} username - Username (generated during signup)
 * @param {string} code - OTP code
 */
export const confirmSignup = async (username, code) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/confirm-signup`, {
    username,
    code,
  });
  return response.data;
};

/**
 * Resend verification code for unconfirmed users
 * @param {string} username - Username or email
 */
export const resendVerificationCode = async (username) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/resend-verification`, {
    username,
  });
  return response.data;
};

/**
 * Complete NEW_PASSWORD_REQUIRED challenge for admin-created users
 * @param {string} username - Cognito username
 * @param {string} session - Challenge session token
 * @param {string} newPassword - The new password to set
 */
export const completeNewPassword = async (username, session, newPassword) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/complete-new-password`, {
    username,
    session,
    newPassword,
  });

  if (response.data.success) {
    localStorage.setItem('cognitoAccessToken', response.data.tokens.accessToken);
    localStorage.setItem('cognitoIdToken', response.data.tokens.idToken);
    localStorage.setItem('cognitoRefreshToken', response.data.tokens.refreshToken);
    localStorage.setItem('cognitoUser', JSON.stringify(response.data.user));
  }

  return response.data;
};

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 */
export const signin = async (email, password) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/signin`, {
    username: email,
    password,
  });

  if (response.data.success) {
    // Store tokens in localStorage
    localStorage.setItem('cognitoAccessToken', response.data.tokens.accessToken);
    localStorage.setItem('cognitoIdToken', response.data.tokens.idToken);
    localStorage.setItem('cognitoRefreshToken', response.data.tokens.refreshToken);
    localStorage.setItem('cognitoUser', JSON.stringify(response.data.user));
  }

  return response.data;
};

/**
 * Get current user info
 */
export const getCurrentUser = async () => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows (Google Sign-In) where access token lacks required scopes
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.get(`${API_BASE_URL}/auth/cognito/user`, {
    headers,
  });

  return response.data;
};

/**
 * Refresh access token
 */
export const refreshToken = async () => {
  const refreshToken = localStorage.getItem('cognitoRefreshToken');
  const userStr = localStorage.getItem('cognitoUser');
  const user = userStr ? JSON.parse(userStr) : null;

  if (!refreshToken) {
    throw new Error('No refresh token found');
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/refresh`, {
    refreshToken,
    username: user?.username, // Pass username for SECRET_HASH calculation
  });

  if (response.data.success) {
    localStorage.setItem('cognitoAccessToken', response.data.tokens.accessToken);
    localStorage.setItem('cognitoIdToken', response.data.tokens.idToken);
  }

  return response.data;
};

/**
 * Sign out
 */
export const signout = async () => {
  const accessToken = localStorage.getItem('cognitoAccessToken');

  if (accessToken) {
    try {
      await axios.post(`${API_BASE_URL}/auth/cognito/signout`, {
        accessToken,
      });
    } catch (error) {
      console.error('Signout error:', error);
    }
  }

  // Clear all Cognito tokens
  localStorage.removeItem('cognitoAccessToken');
  localStorage.removeItem('cognitoIdToken');
  localStorage.removeItem('cognitoRefreshToken');
  localStorage.removeItem('cognitoUser');

  // Also clear legacy tokens for backward compatibility
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userShowName');
  localStorage.removeItem('lastActivity');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('cognitoAccessToken');
};

/**
 * Get stored user from localStorage
 */
export const getStoredUser = () => {
  const userStr = localStorage.getItem('cognitoUser');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Forgot password
 * @param {string} email
 */
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/forgot-password`, {
    username: email,
  });
  return response.data;
};

/**
 * Confirm password reset
 * @param {string} email
 * @param {string} code
 * @param {string} newPassword
 */
export const confirmPasswordReset = async (email, code, newPassword) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/confirm-reset`, {
    username: email,
    code,
    newPassword,
  });
  return response.data;
};

/**
 * Admin: List all users (requires admin access)
 * @param {number} limit - Number of users to fetch
 * @param {string} paginationToken - Token for pagination
 */
export const adminListUsers = async (limit = 60, paginationToken = null) => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  if (paginationToken) params.append('paginationToken', paginationToken);

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.get(`${API_BASE_URL}/auth/cognito/admin/users?${params}`, {
    headers,
  });

  return response.data;
};

/**
 * Admin: Update user attributes (requires admin access)
 * @param {string} username - Username to update
 * @param {object} attributes - Attributes to update (name, active, isAdmin)
 */
export const adminUpdateUser = async (username, attributes) => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.put(`${API_BASE_URL}/auth/cognito/admin/user/${username}`, attributes, {
    headers,
  });

  return response.data;
};

/**
 * Initiate Google OAuth flow via Cognito hosted UI
 * Redirects to Google for authentication through Cognito
 */
export const signInWithGoogle = async () => {
  // Redirect to backend Google OAuth endpoint
  window.location.href = `${API_BASE_URL}/auth/cognito/google`;
};

/**
 * Handle Google OAuth callback
 * This is called when returning from Google auth
 * @param {object} params - URL search params from redirect
 */
export const handleGoogleCallback = async (params) => {
  const googleSuccess = params.get('google_success');
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');
  const refreshToken = params.get('refresh_token');
  const email = params.get('email');
  const name = params.get('name');
  const username = params.get('username');
  const cognitoSub = params.get('cognito_sub');
  const isAdmin = params.get('is_admin') === 'true';
  const active = params.get('active') === 'true';
  const error = params.get('error');

  if (error) {
    throw new Error(params.get('message') || error);
  }

  if (googleSuccess === 'true' && accessToken) {
    // Store tokens in localStorage
    localStorage.setItem('cognitoAccessToken', accessToken);
    localStorage.setItem('cognitoIdToken', idToken);
    if (refreshToken) {
      localStorage.setItem('cognitoRefreshToken', refreshToken);
    }
    localStorage.setItem('cognitoUser', JSON.stringify({
      email,
      name,
      username,
      cognitoSub,
      isAdmin,
      active,
      authProvider: 'google'
    }));

    return {
      success: true,
      user: {
        email,
        name,
        username,
        cognitoSub,
        isAdmin,
        active,
        authProvider: 'google'
      }
    };
  }

  throw new Error('Google authentication failed');
};

/**
 * Verify Google ID token and sign in
 * This is an alternative to the redirect flow
 * @param {string} googleToken - Google ID token from Google Sign-In
 */
export const verifyGoogleToken = async (googleToken) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/google/verify`, {
    googleToken,
  });

  if (response.data.success) {
    // Store tokens in localStorage
    localStorage.setItem('cognitoAccessToken', response.data.tokens.accessToken);
    localStorage.setItem('cognitoIdToken', response.data.tokens.idToken);
    if (response.data.tokens.refreshToken) {
      localStorage.setItem('cognitoRefreshToken', response.data.tokens.refreshToken);
    }
    localStorage.setItem('cognitoUser', JSON.stringify({
      ...response.data.user,
      authProvider: 'google'
    }));
  }

  return response.data;
};

/**
 * Verify TOTP MFA code during sign-in
 * @param {string} session - Session from initial auth response
 * @param {string} code - MFA code from user
 * @param {string} username - Username (required for MFA verification)
 */
export const verifyMFA = async (session, code, username) => {
  const response = await axios.post(`${API_BASE_URL}/auth/cognito/mfa/verify`, {
    session,
    code,
    username, // Pass username for SECRET_HASH calculation
  });

  if (response.data.success) {
    // Store tokens in localStorage
    localStorage.setItem('cognitoAccessToken', response.data.tokens.accessToken);
    localStorage.setItem('cognitoIdToken', response.data.tokens.idToken);
    if (response.data.tokens.refreshToken) {
      localStorage.setItem('cognitoRefreshToken', response.data.tokens.refreshToken);
    }
    localStorage.setItem('cognitoUser', JSON.stringify(response.data.user));
  }

  return response.data;
};

/**
 * Setup TOTP MFA for user
 * @returns {object} { qrCodeDataUrl, secretCode, session }
 */
export const setupMFA = async () => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/mfa/setup`,
    {},
    {
      headers,
    }
  );

  return response.data;
};

/**
 * Verify TOTP MFA setup
 * @param {string} userCode - Verification code
 * @param {string} session - Session from setup response
 */
export const verifyMFASetup = async (userCode, session) => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/mfa/verify-setup`,
    { userCode, session },
    {
      headers,
    }
  );

  return response.data;
};

/**
 * Enable MFA for user
 * @param {string} mfaType - 'TOTP' or 'SMS'
 * @param {string} phoneNumber - Phone number for SMS MFA (required if mfaType is SMS)
 */
export const enableMFA = async (mfaType, phoneNumber = null) => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/mfa/enable`,
    { mfaType, phoneNumber },
    {
      headers,
    }
  );

  return response.data;
};

/**
 * Disable MFA for user
 */
export const disableMFA = async () => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.post(`${API_BASE_URL}/auth/cognito/mfa/disable`,
    {},
    {
      headers,
    }
  );

  return response.data;
};

/**
 * Get MFA status for current user
 */
export const getMFAStatus = async () => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  const idToken = localStorage.getItem('cognitoIdToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // Include ID token for OAuth flows
  if (idToken) {
    headers['x-id-token'] = idToken;
  }

  const response = await axios.get(`${API_BASE_URL}/auth/cognito/mfa/status`, {
    headers,
  });

  return response.data;
};

/**
 * Change password for the currently authenticated user
 */
export const changePassword = async (currentPassword, newPassword) => {
  const accessToken = localStorage.getItem('cognitoAccessToken');
  if (!accessToken) throw new Error('No access token found');

  const response = await axios.post(
    `${API_BASE_URL}/auth/cognito/change-password`,
    { currentPassword, newPassword },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  return response.data;
};
